import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Avatar
} from '@mui/material';
import StepConnector, {
  stepConnectorClasses
} from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Card } from 'src/components/UI/Card';
import { CampaignInProgressStyles } from '../../styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { citationProgress } from 'src/common/Scraper';
import { LocalSeoCommonLoader } from 'src/content/localSeo/LocalSeoCommonLoader';
import dayjs from 'dayjs';

type CampaignStep = {
  id: number;
  label: string;
  subLabel?: string;
};

const CAMPAIGN_STEPS: CampaignStep[] = [
  { id: 1, label: 'Ordered', subLabel: 'Day 1–2' },
  { id: 2, label: 'Reviewing Data', subLabel: 'Day 3–7' },
  { id: 3, label: 'Submitting to Sites', subLabel: 'Day 3–7' },
  { id: 4, label: 'Live Listing Checks', subLabel: 'Day 7–38' },
  { id: 5, label: 'Complete' }
];

const DEFAULT_ACTIVE_STEP_INDEX = 2;
//const createdDate = 1765098641;
//const isCompleted = false;

const CampaignStepperConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    // top: 24
  },
  [`& .${stepConnectorClasses.line}`]: {
    border: 0,
    height: 6,
    borderRadius: 999,
    backgroundColor: 'var(--color-border-medium)'
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: 'var(--color-primary)'
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: 'var(--color-primary)'
  }
}));

type CampaignHeaderProps = {
  businessName: string;
  campaignId: string;
  paymentMethod: string;
  isCompleted: boolean;
};

const CampaignHeader: React.FC<CampaignHeaderProps> = ({
  businessName,
  campaignId,
  paymentMethod,
  isCompleted
}) => {
  return (
    <Card sx={CampaignInProgressStyles.headerCard}>
      <Box sx={CampaignInProgressStyles.headerContentBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            sx={{
              ...CampaignInProgressStyles.heading,
              fontSize: 'var(--font-size-24)',
              lineHeight: 'var(--line-height-32)'
            }}
          >
            {isCompleted
              ? 'Your Campaign Is Completed!'
              : 'Your Campaign Is In Progress!'}
          </Typography>
          {isCompleted && (
            <Avatar
              sx={CampaignInProgressStyles?.completedIcon}
              src="/static/images/icons/check-mark-white.svg"
            />
          )}
        </Box>
        <Typography
          sx={{ ...CampaignInProgressStyles.body, marginTop: '12px' }}
        >
          {isCompleted
            ? 'Your company is successfully completed! You can see how it affected your ranking and listing by clicking on the “View Report” button.'
            : 'Campaign has been successfully purchased and is now in the queue for processing.'}
        </Typography>
      </Box>

      <Box sx={CampaignInProgressStyles.headerDetailsBox}>
        <Typography sx={CampaignInProgressStyles.labelKey}>
          Business Name:
          <Typography
            component="span"
            sx={{ ...CampaignInProgressStyles.body, marginLeft: '6px' }}
          >
            {businessName}
          </Typography>
        </Typography>

        <Typography sx={CampaignInProgressStyles.labelKey}>
          Campaign ID:
          <Typography
            component="span"
            sx={{ ...CampaignInProgressStyles.body, marginLeft: '6px' }}
          >
            {campaignId}
          </Typography>
        </Typography>

        <Typography sx={CampaignInProgressStyles.labelKey}>
          Payment Method:
          <Typography
            component="span"
            sx={{ ...CampaignInProgressStyles.body, marginLeft: '6px' }}
          >
            {paymentMethod}
          </Typography>
        </Typography>
      </Box>
    </Card>
  );
};

type CampaignDeliveryProps = {
  steps: CampaignStep[];
  activeStepIndex: number;
  isCompleted: boolean;
  campaignId: string;
};

const CampaignDelivery: React.FC<CampaignDeliveryProps> = ({
  steps,
  activeStepIndex,
  isCompleted,
  campaignId
}) => {
  const navigate = useNavigate();
  return (
    <Card sx={CampaignInProgressStyles.campaignCard}>
      <Box sx={CampaignInProgressStyles.headerContentBox}>
        <Typography
          sx={{
            ...CampaignInProgressStyles.heading,
            fontSize: 'var(--font-size-24)',
            lineHeight: 'var(--line-height-28)'
          }}
        >
          {'Campaign Delivery'}
        </Typography>

        <Typography
          sx={{ ...CampaignInProgressStyles.body, marginTop: '12px' }}
        >
          We guarantee that at least 70% of your citations will be live within 4
          weeks of submission.
        </Typography>
      </Box>

      <Box sx={CampaignInProgressStyles.stepperWrapper}>
        <Stepper
          activeStep={activeStepIndex}
          alternativeLabel
          connector={<CampaignStepperConnector />}
          sx={CampaignInProgressStyles.stepper}
        >
          {steps.map((step, index) => (
            <Step key={step.id} disabled={index > activeStepIndex}>
              <StepLabel
                icon={
                  isCompleted && step.id === 5 ? (
                    <Avatar
                      sx={CampaignInProgressStyles?.completedStepIcon}
                      src="/static/images/icons/check-mark-white.svg"
                    />
                  ) : (
                    <Box
                      sx={{
                        ...CampaignInProgressStyles.stepIconBase,
                        backgroundColor:
                          index <= activeStepIndex ? 'var(--color-primary)' : '#F4F4F8',
                        border:
                          index <= activeStepIndex
                            ? '0px'
                            : '1px solid #D3D5E0',
                        boxShadow:
                          index <= activeStepIndex
                            ? '0px 2px 6px rgba(18, 2, 66, 0.25)'
                            : 'none',
                        color: index <= activeStepIndex ? 'var(--color-white)' : 'var(--color-text-secondary)'
                      }}
                    >
                      {step.id}
                    </Box>
                  )
                }
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      ...CampaignInProgressStyles.stepLabelTextBase,
                      color: index <= activeStepIndex ? 'var(--color-primary)' : 'var(--color-text-navy)'
                    }}
                  >
                    {step.label}
                  </Typography>
                  {step.subLabel && (
                    <Typography sx={CampaignInProgressStyles.stepSubLabel}>
                      ({step.subLabel})
                    </Typography>
                  )}
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Actions */}
      <Box sx={CampaignInProgressStyles.actionsContainer}>
        <Button
          variant="contained"
          sx={CampaignInProgressStyles.primaryButton}
          onClick={() =>
            navigate('/local-seo/citations/report', {
              state: { campaignId: campaignId }
            })
          }
        >
          View Report
        </Button>

        <Button
          variant="outlined"
          sx={CampaignInProgressStyles.secondaryButton}
          onClick={() => navigate('/local-seo/citations/list')}
        >
          Go To All Campaigns
        </Button>
      </Box>
    </Card>
  );
};

/**
 * Step range configuration for determining active campaign step.
 * Each entry defines the day range and corresponding step index.
 */
type StepRange = {
  minDays: number;
  maxDays: number;
  stepIndex: number;
};

const STEP_RANGES: StepRange[] = [
  { minDays: 0, maxDays: 2, stepIndex: 0 },      // Ordered (Day 1-2)
  { minDays: 3, maxDays: 7, stepIndex: 1 },      // Reviewing Data (Day 3-7)
  { minDays: 3, maxDays: 7, stepIndex: 2 },      // Submitting to Sites (Day 5-6)
  { minDays: 7, maxDays: 38, stepIndex: 3 },     // Live Listing Checks (Day 7-38)
];

const determineActiveStep = (daysSinceConfirmation: number): number => {
  const matchingRange = STEP_RANGES.find(
    (range) => daysSinceConfirmation >= range.minDays && daysSinceConfirmation <= range.maxDays
  );

  return matchingRange?.stepIndex ?? 3;
};

export const CampaignInProgress = () => {
  const { state: locationState } = useLocation();
  const { campaignId } = locationState as { campaignId: string };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [progressInfo, setProgressInfo]: any = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  useEffect(() => {
    if (campaignId) {
      getCitationProgress();
    }
  }, []);

  const normalizeTimestamp = (timestamp: number | string | undefined): dayjs.Dayjs | null => {
    if (!timestamp) return null;
    
    const timestampValue = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
    if (isNaN(timestampValue)) return null;
    
    // If timestamp is 10 digits, it's in seconds (Unix timestamp)
    // Otherwise, assume it's already in milliseconds
    const timestampString = timestampValue.toString();
    const isUnixTimestamp = timestampString.length === 10;
    
    return isUnixTimestamp 
      ? dayjs(timestampValue * 1000)
      : dayjs(timestampValue);
  };

  const calculateDaysSinceConfirmation = (confirmedAt: number | string | undefined): number => {
    const confirmedDate = normalizeTimestamp(confirmedAt);
    if (!confirmedDate) return 0;
    
    return dayjs().diff(confirmedDate, 'day');
  };

  const getCitationProgress = async () => {
    try {
      setLoading(true);
      const response: any = await citationProgress(campaignId);
      
      if (response?.status === 'success' && response?.response) {
        const progressData = response.response;
        setProgressInfo(progressData);
        
        const daysSinceConfirmation = calculateDaysSinceConfirmation(progressData?.confirmed_at);
        const calculatedActiveStepIndex = determineActiveStep(daysSinceConfirmation);
        setActiveStepIndex(calculatedActiveStepIndex);
      }
    } catch (error) {
      console.error('Failed to fetch citation progress:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading ? (
        <>
          <LocalSeoCommonLoader />
        </>
      ) : (
        <>
          <CampaignHeader
            businessName={progressInfo?.businessName}
            campaignId={campaignId || ''}
            paymentMethod={progressInfo?.paymentMethod}
            isCompleted={
              progressInfo?.brightLocalStatus?.lookup_status === 'completed'
            }
          />
          <CampaignDelivery
            steps={CAMPAIGN_STEPS}
            activeStepIndex={activeStepIndex}
            isCompleted={
              progressInfo?.brightLocalStatus?.lookup_status === 'completed'
            }
            campaignId={campaignId || ''}
          />
        </>
      )}
    </React.Fragment>
  );
};
